import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  FileText,
  Target,
  LayoutDashboard,
  GitBranch,
  CheckCircle2,
  AlertCircle,
  X,
  File,
  Loader2,
} from 'lucide-react';
import { PageLayout } from '@/components';
import type { ArchitectureDocType } from '@/lib/types';
import { addApi } from '@/services/addApi';
import { captureError } from '@/lib/sentry';

interface UploadedFile {
  file: File;
  preview?: string;
}

const DocumentImport: React.FC = () => {
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [docType, setDocType] = useState<ArchitectureDocType>('adr');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'success'>('idle');

  const docTypes: { value: ArchitectureDocType; label: string; icon: React.ElementType; description: string }[] = [
    { value: 'vision', label: 'Architecture Vision', icon: Target, description: 'Enterprise-level strategic direction' },
    { value: 'system', label: 'System Architecture', icon: LayoutDashboard, description: 'Comprehensive system documentation' },
    { value: 'solution', label: 'Solution Design', icon: GitBranch, description: 'Design for specific initiatives' },
    { value: 'adr', label: 'Decision Record', icon: FileText, description: 'Individual architecture decision' },
  ];

  const acceptedFileTypes = [
    '.md', '.markdown',
    '.doc', '.docx',
    '.pdf',
    '.txt',
    '.html',
  ];

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(extension)) {
      setError(`Unsupported file type. Please upload: ${acceptedFileTypes.join(', ')}`);
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setError('File size must be less than 10MB');
      return;
    }

    setUploadedFile({ file });
    setError(null);

    // Auto-fill title from filename if empty
    if (!title) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setTitle(nameWithoutExt.replace(/[-_]/g, ' '));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const getContentType = (file: File): string => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      'md': 'text/markdown',
      'markdown': 'text/markdown',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'html': 'text/html',
    };
    return mimeTypes[extension || ''] || file.type || 'application/octet-stream';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      const file = uploadedFile.file;
      const contentType = getContentType(file);

      // Step 1: Get presigned URL
      const { docId, presignedUrl, s3Key } = await addApi.getArchDocUploadUrl(
        file.name,
        contentType,
        docType,
        file.size
      );

      // Step 2: Upload file to S3
      await addApi.uploadArchDoc(presignedUrl, file, contentType, (progress) => {
        setUploadProgress(progress);
      });

      setUploadStatus('processing');

      // Step 3: Save document metadata
      await addApi.saveArchDoc({
        docId,
        docType,
        title: title.trim(),
        description: description.trim() || undefined,
        s3Key,
        contentType,
      });

      setUploadStatus('success');

      // Brief delay to show success state
      setTimeout(() => {
        navigate('/architecture');
      }, 1000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      captureError(err as Error, { component: 'DocumentImport', action: 'upload' });
      setUploadStatus('idle');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <PageLayout>
      {/* Header */}
      <section className="relative pt-20 pb-12 overflow-hidden sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"></div>
        <div className="relative container mx-auto px-6 max-w-4xl">
          <Link
            to="/architecture"
            className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Architecture
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Import Existing Documentation
          </h1>
          <p className="text-lg text-slate-300">
            Upload your existing architecture documents and convert them to SystemDox format.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 min-h-[500px]">
        <div className="container mx-auto px-6 py-8 max-w-4xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Banner */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                <p className="text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* File Upload */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Upload Document</h2>

              {!uploadedFile ? (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  <Upload size={40} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 mb-2">
                    Drag and drop your document here, or{' '}
                    <label className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium">
                      browse
                      <input
                        type="file"
                        className="hidden"
                        accept={acceptedFileTypes.join(',')}
                        onChange={handleFileInputChange}
                      />
                    </label>
                  </p>
                  <p className="text-sm text-slate-500">
                    Supported formats: Markdown, Word, PDF, HTML, Text (max 10MB)
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <File size={24} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{uploadedFile.file.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(uploadedFile.file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}
            </div>

            {/* Document Type */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Document Type</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {docTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = docType === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setDocType(type.value)}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-slate-100'}`}>
                        <Icon size={20} className={isSelected ? 'text-blue-600' : 'text-slate-500'} />
                      </div>
                      <div>
                        <p className={`font-medium ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                          {type.label}
                        </p>
                        <p className="text-sm text-slate-500">{type.description}</p>
                      </div>
                      {isSelected && (
                        <CheckCircle2 size={20} className="text-blue-500 ml-auto flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Document Details */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Document Details</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter document title"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of this document (optional)"
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {uploadStatus !== 'idle' && uploadStatus !== 'success' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 size={20} className="text-blue-600 animate-spin" />
                  <span className="font-medium text-blue-900">
                    {uploadStatus === 'uploading' ? 'Uploading document...' : 'Processing document...'}
                  </span>
                </div>
                {uploadStatus === 'uploading' && (
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Success Message */}
            {uploadStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle2 size={20} className="text-green-600" />
                <span className="font-medium text-green-900">Document imported successfully! Redirecting...</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Link
                to="/architecture"
                className={`px-6 py-3 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors text-center ${
                  isUploading ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={!uploadedFile || !title.trim() || isUploading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Importing...
                  </>
                ) : (
                  'Import Document'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default DocumentImport;
